import { FC, ReactElement, ReactNode, useCallback, useEffect } from 'react';

import { getBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { setDrawerOpen } from 'store/drawerSlice';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import AktivitetBaseInfo from 'components/aktivitetPresentation/AktivitetBaseInfo';
import { GetUtkastAction } from 'components/aktivitetPresentation/GetUtkastAction';
import PubliceradeBebyggelseobjekt from 'components/aktivitetPresentation/publiceradeBebyggelseobjekt/PubliceradeBebyggelseobjekt';
import DeleteAktivitetDialog from 'components/aktivitetRegistrering/inventering/deleteDialogs/DeleteAktivitetDialog';
import { DrawerBody, DrawerFooter, DrawerSection } from 'components/drawerSections/DrawerSections';
import {
  replaceBebyggelseobjektFeaturesInActiveSource,
  replaceUtkastFeaturesInActiveSource
} from 'components/map/Layers';
import { TextButton } from 'components/sharedElements/sharedElements';
import UtkastList from 'components/utkastList/UtkastList';

import { BebyggelseobjektUtkastIndex } from 'models';

import { AktivitetStatusEnum, MapModeEnum } from 'enums';

import { isAnsvarigOrganisation, isAnsvarigOrganisationOrGranskare } from 'utilities/bebyggelseobjektUtilities';
import { isMobile } from 'utilities/isMobile';

const EnkelinventeringPresentation: FC = (): ReactNode => {
  const dispatch = useAppDispatch();
  const { data: aktivitet } = useAppSelector((state: RootState) => state.aktivitet);
  const eTag = aktivitet?.eTag;
  const enkelinventeringAktivitet = aktivitet?.enkelinventering;

  const publiceradeBebyggelseobjekt = useAppSelector((state: RootState) => state.publiceradeBebyggelseobjekt);
  const {
    data: user,
    selected_organisation,
    rolesInSelectedOrganisation
  } = useAppSelector((state: RootState) => state.user);
  const { setDialog, unsetDialog } = useDialog();

  useEffect(() => {
    dispatch(setMapMode(MapModeEnum.INVENTERING_PRESENTATION));
  }, [dispatch]);

  const {
    requestStatus: utkastRequestStatus,
    error: utkastError,
    data: bebyggelseobjektUtkast
  } = useAppSelector((state: RootState) => state.bebyggelseobjektUtkast);

  useInitialFocusOnExtent(
    useCallback(() => {
      replaceBebyggelseobjektFeaturesInActiveSource(publiceradeBebyggelseobjekt.data);
      replaceUtkastFeaturesInActiveSource(bebyggelseobjektUtkast);
    }, [bebyggelseobjektUtkast, publiceradeBebyggelseobjekt.data])
  );

  useEffect(() => {
    if (!enkelinventeringAktivitet || !selected_organisation) {
      return;
    }

    if (
      isAnsvarigOrganisationOrGranskare(enkelinventeringAktivitet, selected_organisation, rolesInSelectedOrganisation)
    ) {
      dispatch(getBebyggelseobjektUtkast(enkelinventeringAktivitet.aktivitet_id));
    }
  }, [enkelinventeringAktivitet, selected_organisation, rolesInSelectedOrganisation, dispatch]);

  useEffect(() => {
    if (publiceradeBebyggelseobjekt.data?.length || bebyggelseobjektUtkast?.length) {
      dispatch(setMapMode(MapModeEnum.INVENTERING_PRESENTATION));
      dispatch(setDrawerOpen('open'));
    }
  }, [publiceradeBebyggelseobjekt, bebyggelseobjektUtkast, dispatch]);

  return (
    <>
      {enkelinventeringAktivitet && (
        <>
          <DrawerBody>
            {enkelinventeringAktivitet && <AktivitetBaseInfo aktivitet={enkelinventeringAktivitet} />}
            <DrawerSection>
              <h2 className="tw-heading-md tw-mb-2">Berört objekt</h2>

              {isAnsvarigOrganisation(enkelinventeringAktivitet, selected_organisation) &&
                !bebyggelseobjektUtkast?.length &&
                enkelinventeringAktivitet?.aktivitetstatus.status === AktivitetStatusEnum.PAGAENDE && (
                  <TextButton
                    icon="raa-icon-add"
                    disabled={!enkelinventeringAktivitet}
                    onClick={() => {
                      dispatch(setMapMode(MapModeEnum.ADD_UTKAST_TO_ENKELINVENTERING));

                      if (isMobile()) {
                        dispatch(setDrawerOpen('minimized'));
                      }
                    }}
                  >
                    Lägg till objekt
                  </TextButton>
                )}

              {bebyggelseobjektUtkast && bebyggelseobjektUtkast.length > 0 && (
                <UtkastList
                  list={bebyggelseobjektUtkast}
                  error={utkastError}
                  user={user}
                  requestStatus={utkastRequestStatus}
                  action={(utkast: BebyggelseobjektUtkastIndex): ReactElement => (
                    <GetUtkastAction utkast={utkast} aktivitet={enkelinventeringAktivitet} />
                  )}
                />
              )}

              {publiceradeBebyggelseobjekt.data && publiceradeBebyggelseobjekt.data.length > 0 && (
                <PubliceradeBebyggelseobjekt
                  list={publiceradeBebyggelseobjekt.data}
                  error={publiceradeBebyggelseobjekt.error}
                  requestStatus={publiceradeBebyggelseobjekt.requestStatus}
                />
              )}
            </DrawerSection>
          </DrawerBody>

          {eTag && enkelinventeringAktivitet && !publiceradeBebyggelseobjekt.data?.length && (
            <DrawerFooter>
              <button
                className="raa-button tw-ml-auto"
                type="button"
                onClick={() => {
                  setDialog(DeleteAktivitetDialog, {
                    eTag,
                    aktivitet: enkelinventeringAktivitet,
                    unsetDialog
                  });
                }}
              >
                Radera
              </button>
            </DrawerFooter>
          )}
        </>
      )}
    </>
  );
};

export default EnkelinventeringPresentation;
